import React, { useCallback, useEffect, useState } from 'react';
import { GlAccountReturn, GlAccountType } from '../clients/rust-api-client';
import { ledgerService } from '../clients/ApiClients';
import { Badge } from '../common/Catalyst/badge';
import { Button } from '../common/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '../common/Catalyst/dialog';
import { Divider } from '../common/Catalyst/divider';
import { Subheading } from '../common/Catalyst/heading';
import {
  Description,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '../common/Catalyst/fieldset';
import { Input } from '../common/Catalyst/input';
import { FieldPicker } from '../common/Custom/FieldPicker';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import { billingErrorMessage } from '../utils/billingHelpers';

interface Props {
  hoaId: string;
  canManage: boolean;
}

const TYPES: { value: GlAccountType; label: string }[] = [
  { value: 'asset', label: 'Asset' },
  { value: 'liability', label: 'Liability' },
  { value: 'equity', label: 'Equity' },
  { value: 'income', label: 'Income' },
  { value: 'expense', label: 'Expense' },
];

const TYPE_ORDER: GlAccountType[] = [
  'asset',
  'liability',
  'equity',
  'income',
  'expense',
];

/**
 * The chart, per association, seeded from the template and editable.
 *
 * GL_BUILD_PLAN names this the thing a customer will find wrong first —
 * two sample associations under one management company model the
 * reserve contribution differently — so editing has to be genuinely
 * easy rather than theoretically possible.
 */
const ChartOfAccounts: React.FC<Props> = ({ hoaId, canManage }) => {
  const [accounts, setAccounts] = useState<GlAccountReturn[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<GlAccountReturn | null>(null);
  const [editName, setEditName] = useState('');
  const [adding, setAdding] = useState(false);
  const [newNumber, setNewNumber] = useState('');
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<GlAccountType>('expense');
  const [roleTarget, setRoleTarget] = useState<GlAccountReturn | null>(null);
  const [roleToMove, setRoleToMove] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await ledgerService.listAccounts({ hoaId });
      setAccounts(data.accounts);
    } catch (err) {
      setError(billingErrorMessage(err, "Couldn't load the chart."));
    } finally {
      setLoading(false);
    }
  }, [hoaId]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (fn: () => Promise<unknown>, failure: string) => {
    try {
      setBusy(true);
      setError(null);
      await fn();
      await load();
    } catch (err) {
      setError(billingErrorMessage(err, failure));
    } finally {
      setBusy(false);
    }
  };

  const rename = () => {
    if (!editing) return;
    void act(async () => {
      await ledgerService.updateAccount({
        hoaId,
        accountId: editing.id,
        glAccountUpdate: { name: editName.trim() },
      });
      setEditing(null);
    }, "Couldn't rename that account.");
  };

  const setActive = (account: GlAccountReturn, is_active: boolean) =>
    void act(
      () =>
        ledgerService.updateAccount({
          hoaId,
          accountId: account.id,
          glAccountUpdate: { is_active },
        }),
      "Couldn't change that account."
    );

  /**
   * Take a posting role off whichever account holds it and put it on
   * this one.
   *
   * A **move**, never a detach. `update_account` treats a null
   * `posting_role` as "no change", so there is no detach path — and
   * rightly: a role is what the emitters resolve, and an unheld role
   * means every posting that needs it fails. The backend does the
   * hand-off in one call.
   */
  const moveRole = () => {
    if (!roleTarget || !roleToMove) return;
    void act(async () => {
      await ledgerService.updateAccount({
        hoaId,
        accountId: roleTarget.id,
        glAccountUpdate: { posting_role: roleToMove },
      });
      setRoleTarget(null);
      setRoleToMove('');
    }, "Couldn't move that role.");
  };

  const add = () =>
    void act(async () => {
      await ledgerService.createAccount({
        hoaId,
        glAccountCreate: {
          account_number: newNumber.trim(),
          name: newName.trim(),
          account_type: newType,
          description: null,
          hoa_bank_account_id: null,
          normal_balance: null,
          parent_id: null,
        },
      });
      setAdding(false);
      setNewNumber('');
      setNewName('');
    }, "Couldn't add that account.");

  // Only roles this association's chart actually carries. The full
  // list lives in the backend's `roles::ALL` and is not on the wire —
  // and a role nothing holds is not one anybody can move.
  const rolesInChart = Array.from(
    new Set(accounts.map((a) => a.posting_role).filter((r): r is string => !!r))
  ).sort();

  const holderOf = (role: string): string => {
    const holder = accounts.find((a) => a.posting_role === role);
    return holder ? `${holder.account_number} ${holder.name}` : 'nothing';
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="That did not work"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={10000}
            isInline={true}
          />
        </div>
      )}

      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          Grouped by type, in account-number order.
        </p>
        {canManage && (
          <Button color="sky" onClick={() => setAdding(true)}>
            Add account
          </Button>
        )}
      </div>

      {TYPE_ORDER.map((type) => {
        const rows = accounts
          .filter((a) => a.account_type === type)
          .sort((a, b) => a.account_number.localeCompare(b.account_number));
        if (rows.length === 0) return null;
        return (
          <div key={type} className="mt-6">
            <Subheading>
              {TYPES.find((t) => t.value === type)?.label}
            </Subheading>
            <Divider className="mt-2 mb-1" />
            {rows.map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between gap-3 py-1.5 text-sm text-zinc-950 dark:text-white border-b border-zinc-100 dark:border-zinc-800"
              >
                <span
                  className={
                    a.is_active
                      ? 'truncate'
                      : 'truncate text-zinc-400 dark:text-zinc-500'
                  }
                >
                  <span className="tabular-nums">{a.account_number}</span>{' '}
                  {a.name}
                  {a.description && (
                    <span className="ml-2 text-xs text-zinc-500 dark:text-zinc-400">
                      {a.description}
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  {/*
                    What lets a later template revision reach untouched
                    rows without overwriting an association's own edits.
                    An account somebody has edited should visibly stop
                    being the template's.
                  */}
                  {a.is_template_seeded && (
                    <Badge color="zinc">from the template</Badge>
                  )}
                  {/*
                    A role is what the emitters resolve, so detaching one
                    silently breaks posting. Shown on the row, and moving
                    it is a deliberate action with a confirm rather than
                    an inline edit.
                  */}
                  {a.posting_role && (
                    <Badge color="sky">{a.posting_role}</Badge>
                  )}
                  {!a.is_active && <Badge color="zinc">inactive</Badge>}
                  {canManage && (
                    <>
                      <Button
                        plain
                        onClick={() => {
                          setEditing(a);
                          setEditName(a.name);
                        }}
                      >
                        Rename
                      </Button>
                      {rolesInChart.length > 0 && (
                        <Button
                          plain
                          onClick={() => {
                            setRoleTarget(a);
                            setRoleToMove(rolesInChart[0]);
                          }}
                        >
                          Move a role here
                        </Button>
                      )}
                      <Button
                        plain
                        disabled={busy}
                        onClick={() => setActive(a, !a.is_active)}
                      >
                        {a.is_active ? 'Deactivate' : 'Reactivate'}
                      </Button>
                    </>
                  )}
                </span>
              </div>
            ))}
          </div>
        );
      })}

      <Dialog open={!!editing} onClose={() => setEditing(null)}>
        <DialogTitle>Rename account</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
                {editing?.is_template_seeded && (
                  <Description>
                    This account came from the template. Renaming it makes it
                    this association's own, and a later template revision will
                    leave it alone.
                  </Description>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setEditing(null)}>
            Cancel
          </Button>
          <Button
            color="sky"
            disabled={busy || !editName.trim()}
            onClick={rename}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!roleTarget} onClose={() => setRoleTarget(null)}>
        <DialogTitle>Move a posting role</DialogTitle>
        <DialogDescription>
          A role is what the ledger resolves when it posts. Moving one takes it
          off the account that holds it now — which is how an association points
          late fees at its own account rather than the template's.
        </DialogDescription>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Role</Label>
                <FieldPicker
                  label="Role"
                  value={roleToMove}
                  onChange={setRoleToMove}
                  options={rolesInChart.map((role) => ({
                    value: role,
                    label: `${role} — currently on ${holderOf(role)}`,
                  }))}
                />
              </Field>
            </FieldGroup>
          </Fieldset>
          <p className="mt-4 text-sm text-zinc-950 dark:text-white">
            It will move to{' '}
            <strong>
              {roleTarget?.account_number} {roleTarget?.name}
            </strong>
            . Exactly one account carries a role at a time, so nothing is left
            without one.
          </p>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setRoleTarget(null)}>
            Cancel
          </Button>
          <Button color="sky" disabled={busy || !roleToMove} onClick={moveRole}>
            Move role
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={adding} onClose={() => setAdding(false)}>
        <DialogTitle>Add an account</DialogTitle>
        <DialogDescription>
          The number decides where it sorts; the type decides which report it
          lands on.
        </DialogDescription>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Account number</Label>
                <Input
                  value={newNumber}
                  onChange={(e) => setNewNumber(e.target.value)}
                  placeholder="62100"
                />
              </Field>
              <Field>
                <Label>Name</Label>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Reserve Contributions"
                />
              </Field>
              <Field>
                <Label>Type</Label>
                <FieldPicker
                  label="Type"
                  value={newType}
                  onChange={(v) => setNewType(v as GlAccountType)}
                  options={TYPES}
                />
                {/*
                  Normal balance is left to the backend's default for
                  the type. `40200 - Transfer to Reserves` is
                  income-typed and debit-balanced, which is why the
                  column is stored per account — but a contra account is
                  rare enough that offering the choice here would
                  mislead more people than it helps.
                */}
                <Description>
                  The usual balance for the type is assumed. A contra account
                  needs a change afterwards.
                </Description>
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setAdding(false)}>
            Cancel
          </Button>
          <Button
            color="sky"
            disabled={busy || !newNumber.trim() || !newName.trim()}
            onClick={add}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ChartOfAccounts;
